import axios from 'axios';
import { useEffect, useState } from 'react';

import Card from './AppointmentCardStyles';
import config from '../../config';
import {
  appointmentReconfirmation,
  cancelSlotRequest,
  payUpdatedAmountForAppointment,
} from '../../services';

import userIcon from '../../images/common/user-icon.svg';
import redClockIcon from '../../images/common/red-clock.svg';
import greenClockIcon from '../../images/common/green-clock.svg';
import clockBwIcon from '../../images/common/clock-icon-bw.svg';
import doctorIcon from '../../images/common/doctor-icon.svg';
import doctorBwIcon from '../../images/common/doctor-icon-bw.svg';
import currencyIcon from '../../images/common/currency.svg';
import currencyBwIcon from '../../images/common/currency-icon-bw.svg';
import calendarIcon from '../../images/common/calendar-icon.svg';
import calendarBwIcon from '../../images/common/calendar-icon-bw.svg';
import ellipseIcon from '../../images/common/ellipse.svg';
import cautionIcon from '../../images/consultations/red-cross.svg';
import greenTickIcon from '../../images/common/green-tick.svg';
import headerHandshake from '../../images/common/handshake.svg';
import headerCancel from '../../images/common/header-cancel.svg';
import headerConfirm from '../../images/common/header-confirm.svg';
import headerWallet from '../../images/common/header-wallet.svg';
import buildingIcon from '../../images/consultations/new-flow/building.svg';
import hashIcon from '../../images/hash.svg';
import centerBuildingIcon from '../../images/center-building.svg';
import greyCenterIcon from '../../images/grey-center.svg';
import syringe from '../../images/vaccination/syringe-small.svg';
import partnerIcon from '../../images/vaccination/partner.svg';
import doorIcon from '../../images/consultations/doorIcon.svg';

const toCurr = (curr, decimal = 0) =>
  ((typeof curr === 'string' ? +curr : curr) || 0).toLocaleString('en-IN', {
    maximumFractionDigits: decimal,
    minimumFractionDigits: decimal,
    style: 'currency',
    currency: 'INR',
  });

const limitText = (text, limit = 4) => {
  if (!text || typeof text !== 'string') {
    return text;
  }

  const words = text.split(' ');
  if (words.length > limit) {
    return `${words.slice(0, limit).join(' ')}...`;
  }

  return text;
};

export const dentalCardParams = [
  { name: 'Patient', valueKey: 'patientName', icon: userIcon },
  {
    name: 'Appointment ID - ',
    nameKey: 'requestId',
    value: 'Booked on ',
    valueKey: 'bookingDate',
    icon: hashIcon,
  },
  {
    name: 'Doctor Name',
    valueKey: 'doctorName',
    prevValueKey: 'prevDoctorName',
    newValueKey: 'newDoctorName',
    icon: doctorIcon,
    prevIcon: doctorBwIcon,
  },
  {
    name: 'Consultation Fees',
    valueKey: 'doctorFee',
    prevValueKey: 'prevFees',
    newValueKey: 'newFees',
    icon: currencyIcon,
    prevIcon: currencyBwIcon,
    valueFormatter: toCurr,
  },
  {
    name: 'Date of Appointment & Time',
    valueKey: 'appointmentDate',
    prevValueKey: 'previousDate',
    newValueKey: 'newDate',
    icon: calendarIcon,
    prevIcon: calendarBwIcon,
  },
];

export const dentalStatusCardParams = [
  { name: 'Patient', valueKey: 'patientName', icon: userIcon },
  {
    name: 'Appointment ID - ',
    nameKey: 'mdpAppointmentId',
    value: 'Booked on ',
    valueKey: 'appointmentBookedOn',
    icon: hashIcon,
  },
  {
    name: 'Center Details',
    valueKey: 'clinicAddress',
    prevValueKey: 'prevNetworkCenter',
    newValueKey: 'newNetworkCenter',
    icon: centerBuildingIcon,
    prevIcon: greyCenterIcon,
    tooltipKey: 'centerAddress',
  },
  {
    name: 'Date of Appointment & Time',
    valueKey: 'appointmentTime',
    prevValueKey: 'previousDate',
    newValueKey: 'newDate',
    icon: calendarIcon,
    prevIcon: calendarBwIcon,
  },
];

export const cardParams = [
  { name: 'Patient', valueKey: 'patientName', icon: userIcon },
  {
    name: 'Appointment ID - ',
    nameKey: 'requestId',
    value: 'Booked on ',
    valueKey: 'bookingDate',
    icon: hashIcon,
  },
  {
    name: 'Center Details',
    valueKey: 'centerName',
    prevValueKey: 'prevNetworkCenter',
    newValueKey: 'newNetworkCenter',
    icon: centerBuildingIcon,
    prevIcon: greyCenterIcon,
    // tooltipKey: 'centerAddress',
    valueFormatter: limitText,
  },
  {
    name: 'Doctor Name',
    valueKey: 'doctorName',
    prevValueKey: 'prevDoctorName',
    newValueKey: 'newDoctorName',
    icon: doctorIcon,
    prevIcon: doctorBwIcon,
    verticalValueKey: 'vertical',
  },
  {
    name: 'Doctor Details',
    valueKey: 'vertical',
    prevValueKey: 'verticalName',
    newValueKey: 'verticalName',
    icon: doctorIcon,
    prevIcon: doctorBwIcon,
  },
  {
    name: 'Consultation Fees',
    valueKey: 'doctorFees',
    prevValueKey: 'prevFees',
    newValueKey: 'newFees',
    icon: currencyIcon,
    prevIcon: currencyBwIcon,
    valueFormatter: toCurr,
  },
  {
    name: 'Date of Appointment',
    valueKey: 'appointmentDate',
    prevValueKey: 'previousDate',
    newValueKey: 'newDate',
    icon: calendarIcon,
    prevIcon: calendarBwIcon,
  },
  {
    name: 'Time Slot',
    valueKey: 'timeSlot',
    prevValueKey: 'prevSlot',
    newValueKey: 'newSlot',
    icon: redClockIcon,
    prevIcon: clockBwIcon,
  },
  {
    name: 'Slot Type',
    valueKey: 'slotType',
    icon: doorIcon,
    tooltipKey: 'centerAddress',
  },
];

export const doctorCardParams = [
  { name: 'Patient', valueKey: 'patientName', icon: userIcon },
  {
    name: 'Consultation ID - ',
    nameKey: 'referenceId',
    value: 'Booked on ',
    valueKey: 'bookedOn',
    icon: greenClockIcon,
  },
  {
    name: 'Center Details',
    valueKey: 'centerName',
    prevValueKey: 'prevDoctorName',
    icon: buildingIcon,
    prevIcon: doctorBwIcon,
  },
  {
    name: 'Vertical',
    valueKey: 'vertical',
    prevValueKey: 'prevDoctorName',
    icon: buildingIcon,
    prevIcon: doctorBwIcon,
  },
  {
    name: 'Consultation Date & Time',
    valueKey: 'appointmentOn',
    prevValueKey: 'prevAppointmentDate',
    icon: calendarIcon,
    prevIcon: calendarBwIcon,
  },
];

export const vaccinationCardParams = [
  { name: 'Patient: ', valueKey: 'patientName', icon: userIcon },
  {
    name: 'Order ID: ',
    nameKey: 'requestId',
    value: '',
    valueKey: 'orderId',
    icon: greenClockIcon,
  },
  { name: 'Vaccination: ', valueKey: 'vaccineNamesArray', icon: syringe },
  {
    name: 'Date & Time Slot: ',
    valueKey: 'requestedOn',
    prevValueKey: 'previousDate',
    newValueKey: 'newDate',
    icon: calendarIcon,
    prevIcon: calendarBwIcon,
    value: '',
  },
  {
    name: 'Center: ',
    valueKey: 'centerName',
    icon: partnerIcon,
  },
];

export const useImages = (icon, cardType) => {
  const [label, setLabel] = useState('');
  const [header, setHeader] = useState('');

  useEffect(() => {
    if (icon === 'cross') setLabel(cautionIcon);
    else if (icon === 'processing') setLabel(ellipseIcon);
    else if (icon === 'tick') setLabel(greenTickIcon);
    else setLabel('');
  }, [icon]);

  useEffect(() => {
    if (cardType === 'cancelled') setHeader(headerCancel);
    else if (cardType === 'request-cancelled') setHeader(headerCancel);
    else if (cardType === 'appointment-reschedule-copay')
      setHeader(headerWallet);
    else if (cardType === 'appointment-reschedule') setHeader(headerHandshake);
    else if (cardType === 'request-confirmed') setHeader(headerHandshake);
    else setHeader(headerConfirm);
  }, [cardType]);

  return [label, header];
};

export const LabelJSX = ({ label, hex, icon }) => {
  const [labelIcon, _] = useImages(icon);

  return (
    <Card.LabelContainer>
      <Card.Logo
        src={labelIcon}
        alt={label}
        style={{
          marginRight: '2.5px',
          animation:
            icon === 'processing' ? 'icon-spin 2s infinite linear' : '',
        }}
      />
      <Card.LabelText style={{ color: hex }}>{label}</Card.LabelText>
    </Card.LabelContainer>
  );
};

export const alterRescheduleRequest = (
  requestId,
  authToken,
  onReload,
  onClose,
  modalType
) => {
  let url = `${config.apiBaseUrl}/hospital`;
  if (modalType === 'cancel') url += `/cancel-rescheduled/${requestId}`;
  else if (modalType === 'confirm') url += `/confirm-rescheduled/${requestId}`;

  return axios
    .get(url, {
      headers: { authorization: authToken },
    })
    .then(() => {
      onReload();
    })
    .catch((err) => {
      console.log(err);
    })
    .finally(() => {
      onClose();
    });
};

export const cancelNormalAppointment = (
  requestId,
  cancelCharge,
  authToken,
  onReload,
  onClose
) => {
  cancelSlotRequest(requestId, cancelCharge || 0, authToken)
    .then(() => {
      onReload();
    })
    .catch((err) => {
      console.log(err);
    })
    .finally(() => {
      onClose();
    });
};

export const reconfirmNormalAppointment = (
  requestId,
  authToken,
  onReload,
  onClose
) => {
  appointmentReconfirmation(requestId, authToken)
    .then(() => {
      onReload();
    })
    .catch((err) => {
      console.log(err);
    })
    .finally(() => {
      onClose();
    });
};
